import React, { useEffect, useRef, useState } from "react";
import {
    Typography,
    Container,
    Paper,
    TextField,
    Button,
    Stack,
    Divider,
    Box,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import SendIcon from "@mui/icons-material/Send";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import LinkIcon from "@mui/icons-material/Link";

/**
 * Realtime WS Client rewritten from Python to React + Material UI.
 *
 * Key features:
 * - WebSocket transport compatible with the Python message schema
 *   (types: user_text, user_audio_begin / user_audio_chunk / user_audio_end).
 * - Microphone capture (Float32 -> PCM16 -> base64) ~80ms chunks.
 * - PCM16/24kHz mono audio playback via AudioWorklet with smooth fade-out / fade-in
 *   and the "cut on next audio after done" behavior.
 * - Minimal, clean MUI UI with connect / voice start-stop / text send / live log.
 *
 * Single file. The audio worklet processors are injected from Blob URLs.
 */

const DEFAULT_WS_URL =
    (import.meta && import.meta.env && import.meta.env.VITE_WS_URL) ||
    "wss://aiamo.e-perepletspb.ru/chat-api/ws/realtime-proxy?phone=+79009000011";

// wss://aiamo.e-perepletspb.ru/chat-api/ws/realtime-proxy
// ws://localhost:8080/chat-api/ws/realtime-proxy?phone=+79009000011

const SAMPLE_RATE = 24000;
const CHANNELS = 1;
const BYTES_PER_SAMPLE = 2; // int16
const CHUNK_MS = 80; // capture chunk

// Fade durations (ms) — aligned with the Python version
const FADE_OUT_MS = 8;
const FADE_IN_MS = 6;

// === Helpers ===
function floatTo16PCM(float32) {
    const out = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
        let s = Math.max(-1, Math.min(1, float32[i]));
        out[i] = (s < 0 ? s * 0x8000 : s * 0x7fff) | 0;
    }
    return out;
}

function int16ToFloat32(int16) {
    const out = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
        out[i] = int16[i] / 32768;
    }
    return out;
}

function base64FromInt16(int16) {
    // Convert Int16Array to binary string for btoa
    let binary = "";
    const bytes = new Uint8Array(int16.buffer);
    const chunk = 0x8000; // avoid call stack limits
    for (let i = 0; i < bytes.length; i += chunk) {
        const sub = bytes.subarray(i, i + chunk);
        binary += String.fromCharCode.apply(null, sub);
    }
    return btoa(binary);
}

function int16FromBase64(b64) {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Int16Array(bytes.buffer);
}

// === AudioWorklet code (player) — injected via Blob URL ===
const playerWorkletURL = URL.createObjectURL(
    new Blob([
        `class PCMPlayerProcessor extends AudioWorkletProcessor {
      constructor() {
        super();
        this.queue = []; // of Float32Array
        this.readIndex = 0; // position within head chunk
        this.cutPending = false; // set true to start fade-out then clear
        this.fadeOutSamples = Math.max(1, (${SAMPLE_RATE} * ${FADE_OUT_MS}) / 1000 | 0);
        this.fadeInSamples = Math.max(1, (${SAMPLE_RATE} * ${FADE_IN_MS}) / 1000 | 0);
        this.fadeOutRem = 0; // remaining samples in current fade-out
        this.fadeInRem = 0;  // remaining samples in current fade-in
        this.port.onmessage = (e) => {
          const { type, payload } = e.data || {};
          if (type === 'push' && payload && payload.length) {
            this.queue.push(payload);
          } else if (type === 'cut') {
            // trigger a gentle fade-out; after finished — drop the queue
            this.cutPending = true;
            this.fadeOutRem = this.fadeOutSamples;
          }
        };
      }

      _pullSample() {
        if (!this.queue.length) return 0;
        const chunk = this.queue[0];
        const sample = chunk[this.readIndex++] || 0;
        if (this.readIndex >= chunk.length) {
          this.queue.shift();
          this.readIndex = 0;
        }
        return sample;
      }

      process(inputs, outputs) {
        const output = outputs[0];
        const channel = output[0]; // mono
        const n = channel.length;

        for (let i = 0; i < n; i++) {
          let s = this._pullSample();

          // Apply fade-out if requested
          if (this.fadeOutRem > 0) {
            const idx = this.fadeOutSamples - this.fadeOutRem; // progressed
            const gain = Math.max(0, 1 - (idx + 1) / this.fadeOutSamples);
            s *= gain;
            this.fadeOutRem--;
            if (this.fadeOutRem === 0 && this.cutPending) {
              // clear any remaining audio
              this.queue = [];
              this.readIndex = 0;
              this.cutPending = false;
              // enable fade-in for next content
              this.fadeInRem = this.fadeInSamples;
            }
          }

          // Apply fade-in if active
          if (this.fadeInRem > 0) {
            const idx = this.fadeInSamples - this.fadeInRem;
            const gain = Math.min(1, (idx + 1) / this.fadeInSamples);
            s *= gain;
            this.fadeInRem--;
          }

          channel[i] = s;
        }

        return true;
      }
    }
    registerProcessor('pcm-player', PCMPlayerProcessor);`
    ], { type: "text/javascript" })
);

export default function OpenAIRealtimeDemo() {
    // UI state
    const [wsUrl, setWsUrl] = useState(DEFAULT_WS_URL);
    const [connected, setConnected] = useState(false);
    const [recording, setRecording] = useState(false);
    const [autoScroll, setAutoScroll] = useState(true);
    const [inputText, setInputText] = useState("");
    const [log, setLog] = useState("");

    // WS + audio refs
    const wsRef = useRef(/** @type {WebSocket|null} */(null));
    const ctxRef = useRef(/** @type {AudioContext|null} */(null));
    const playerNodeRef = useRef(/** @type {AudioWorkletNode|null} */(null));
    const micStreamRef = useRef(/** @type {MediaStream|null} */(null));
    const micProcRef = useRef(/** @type {ScriptProcessorNode|null} */(null));
    const cutOnNextAudioRef = useRef(false);
    const logViewRef = useRef(null);

    // Init AudioContext + Worklet lazily
    const ensureAudio = async () => {
        if (ctxRef.current) return ctxRef.current;
        const ctx = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: SAMPLE_RATE,
        });
        await ctx.audioWorklet.addModule(playerWorkletURL);
        const node = new AudioWorkletNode(ctx, "pcm-player", { numberOfOutputs: 1, outputChannelCount: [1] });
        node.connect(ctx.destination);
        ctxRef.current = ctx;
        playerNodeRef.current = node;
        return ctx;
    };

    const appendLog = (s) => {
        setLog((prev) => prev + s);
    };

    useEffect(() => {
        if (!autoScroll) return;
        const el = logViewRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [log, autoScroll]);

    const connect = async () => {
        if (connected) return;
        appendLog(`[i] Connecting to ${wsUrl} ...\n`);
        try {
            const ws = new WebSocket(wsUrl);
            ws.binaryType = "arraybuffer";

            ws.onopen = () => {
                setConnected(true);
                appendLog("[i] Connected.\n • введите текст и Send — отправится как user_text\n • Voice Start / Voice Stop\n");
            };

            ws.onclose = () => {
                setConnected(false);
                appendLog("\n[ws] connection closed\n");
            };

            ws.onerror = (e) => {
                appendLog(`\n[ws] error: ${e?.message || e}\n`);
            };

            ws.onmessage = async (evt) => {
                let msg;
                try {
                    msg = JSON.parse(typeof evt.data === 'string' ? evt.data : new TextDecoder().decode(evt.data));
                } catch (e) {
                    appendLog(`\n[!] non-JSON: ${String(evt.data).slice(0, 120)}\n`);
                    return;
                }

                const type = msg?.type;
                if (type === "delta") {
                    const text = msg?.text || "";
                    if (text) appendLog(text);
                    return;
                }
                if (type === "audio") {
                    // If done was seen, the first new audio chunk softly cuts the previous
                    if (cutOnNextAudioRef.current) {
                        cutOnNextAudioRef.current = false;
                        // trigger fade-out, clear, fade-in in the worklet
                        await ensureAudio();
                        playerNodeRef.current?.port.postMessage({ type: "cut" });
                    }
                    const b64 = msg?.base64;
                    if (b64) {
                        try {
                            const i16 = int16FromBase64(b64);
                            const f32 = int16ToFloat32(i16);
                            await ensureAudio();
                            playerNodeRef.current?.port.postMessage({ type: "push", payload: f32 });
                        } catch (e) {
                            appendLog(`\n[!] bad audio chunk: ${e}\n`);
                        }
                    }
                    return;
                }
                if (type === "done") {
                    appendLog("\n[done]\n");
                    // allow current audio to play out unless new audio arrives
                    cutOnNextAudioRef.current = true;
                    return;
                }
                if (type === "error") {
                    appendLog(`\n[server error] ${msg?.message || ''}\n`);
                    return;
                }
            };

            wsRef.current = ws;
        } catch (e) {
            appendLog(`[!] connect error: ${e}\n`);
        }
    };

    const disconnect = () => {
        if (recording) stopVoice();
        wsRef.current?.close();
        wsRef.current = null;
    };

    // === Voice capture ===
    const startVoice = async () => {
        if (!connected || recording) return;
        await ensureAudio();
        if (ctxRef.current?.state === "suspended") await ctxRef.current.resume();

        // Notify server
        wsRef.current?.send(
            JSON.stringify({ type: "user_audio_begin", format: "pcm16", sample_rate: SAMPLE_RATE })
        );


        // getUserMedia + ScriptProcessor
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: { channelCount: 1, sampleRate: SAMPLE_RATE },
        });
        const source = ctxRef.current.createMediaStreamSource(stream);

// createScriptProcessor требует размер буфера = 0 или степень двойки (256–16384)
        const target = Math.floor((ctxRef.current.sampleRate * CHUNK_MS) / 1000);
        const clamped = Math.min(Math.max(target, 256), 16384);
        const pow2 = 1 << Math.ceil(Math.log2(clamped));
        const blockSize = pow2; // e.g. 2048 при ~80 мс на 24 кГц
        console.debug("[mic] chosen blockSize=", blockSize, "target=", target, "ctx SR=", ctxRef.current.sampleRate);

        const proc = ctxRef.current.createScriptProcessor(blockSize, 1, 1);

        proc.onaudioprocess = (ev) => {
            const input = ev.inputBuffer.getChannelData(0); // Float32
            // copy because underlying buffer is reused
            const copy = new Float32Array(input.length);
            copy.set(input);
            const i16 = floatTo16PCM(copy);
            const b64 = base64FromInt16(i16);
            try {
                wsRef.current?.send(JSON.stringify({ type: "user_audio_chunk", audio_base64: b64 }));
            } catch (e) {
                appendLog(`[ws] send audio chunk error: ${e}\n`);
            }
        };

        source.connect(proc);
        proc.connect(ctxRef.current.destination); // not audible; needed on some platforms

        micStreamRef.current = stream;
        micProcRef.current = proc;
        setRecording(true);
        appendLog("[i] запись начата\n");
    };

    const stopVoice = async () => {
        if (!recording) return;
        try {
            wsRef.current?.send(JSON.stringify({ type: "user_audio_end" }));
        } catch {}
        // teardown
        try { micProcRef.current?.disconnect(); } catch {}
        try { const tracks = micStreamRef.current?.getTracks?.() || []; tracks.forEach(t => t.stop()); } catch {}
        micProcRef.current = null;
        micStreamRef.current = null;
        setRecording(false);
        appendLog("[i] запись остановлена, ждём ответ...\n");
    };

    const sendText = () => {
        const text = inputText.trim();
        if (!text || !connected) return;
        try {
            wsRef.current?.send(JSON.stringify({ type: "user_text", text }));
            setInputText("");
        } catch (e) {
            appendLog(`[ws] send error: ${e}\n`);
        }
    };

    useEffect(() => {
        return () => {
            // cleanup on unmount
            try { micProcRef.current?.disconnect(); } catch {}
            try { const tracks = micStreamRef.current?.getTracks?.() || []; tracks.forEach(t => t.stop()); } catch {}
            wsRef.current?.close();
            playerNodeRef.current?.disconnect();
            ctxRef.current?.close();
        };
    }, []);

    return (
        <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>

            <Container maxWidth="md" sx={{ py: 3 }}>
                <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                        <TextField
                            label="WebSocket URL"
                            fullWidth
                            value={wsUrl}
                            onChange={(e) => setWsUrl(e.target.value)}
                            size="small"
                        />
                        {!connected ? (
                            <Button startIcon={<LinkIcon />} variant="contained" onClick={connect}>
                                Connect
                            </Button>
                        ) : (
                            <Button startIcon={<LinkOffIcon />} color="warning" variant="contained" onClick={disconnect}>
                                Disconnect
                            </Button>
                        )}
                    </Stack>
                </Paper>

                <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                        <TextField
                            label="Type message"
                            fullWidth
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            size="small"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey || !e.shiftKey)) {
                                    e.preventDefault();
                                    sendText();
                                }
                            }}
                        />
                        <Button endIcon={<SendIcon />} variant="contained" onClick={sendText} disabled={!connected}>
                            Send
                        </Button>
                        {!recording ? (
                            <Button startIcon={<PlayArrowIcon />} variant="outlined" onClick={startVoice} disabled={!connected}>
                                Voice Start
                            </Button>
                        ) : (
                            <Button startIcon={<StopIcon />} color="error" variant="outlined" onClick={stopVoice}>
                                Voice Stop
                            </Button>
                        )}
                    </Stack>
                </Paper>

                <Paper sx={{ p: 2 }} variant="outlined">
                    <Typography variant="subtitle2" gutterBottom>Log</Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Box
                        ref={logViewRef}
                        sx={{
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                            whiteSpace: "pre-wrap",
                            bgcolor: "#0b1020",
                            color: "#d8dee9",
                            p: 2,
                            borderRadius: 1,
                            height: 360,
                            overflow: "auto",
                        }}
                    >
                        {log || "(empty)"}
                    </Box>
                </Paper>

                <Box sx={{ mt: 2, color: "text.secondary" }}>
                    <Typography variant="caption">
                        • Playback: 24kHz mono PCM16 → Float32 via AudioWorklet with fade-out ({FADE_OUT_MS}ms) / fade-in ({FADE_IN_MS}ms).<br/>
                        • Capture: getUserMedia → ScriptProcessor (Float32) → PCM16 → base64 chunks (~{CHUNK_MS}ms).<br/>
                        • Behavior: after 'done' next audio softly cuts current and enables fade-in for the new track.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}
